<template>
	<section
		v-if="hasProfile"
		ref="profileSectionRef"
		class="teacher-info"
		:class="{
			'teacher-info--area': isAreaTeacher,
			'teacher-info--creator': isCreatorMode,
			'teacher-info--teacher': isTeacherMode,
		}"
	>
		<div v-if="isAreaTeacher" class="teacher-info__area-card">
			<img
				v-if="areaHeaderImage"
				class="teacher-info__area-image"
				:src="areaHeaderImage"
				:alt="profileName"
			/>
			<div v-else class="teacher-info__area-fallback"></div>

			<div class="teacher-info__area-content">
				<h1 class="teacher-info__area-name">{{ profileName }}</h1>
				<ul v-if="profileTagItems.length" class="teacher-info__area-tags" role="list">
					<li v-for="tag in profileTagItems" :key="tag.text" class="teacher-info__area-tag">
						{{ tag.text }}
					</li>
				</ul>
				<p v-if="profileIntro" class="teacher-info__area-intro">{{ profileIntro }}</p>
			</div>
		</div>

		<template v-else-if="isCreatorMode">
			<div class="teacher-info__profile">
				<div class="teacher-info__avatar-wrap">
					<img class="teacher-info__avatar" :src="avatarSrc" :alt="profileName" />
				</div>
				<h1 class="teacher-info__name">{{ profileName }}</h1>
			</div>

			<div class="teacher-info__stats-row">
				<div class="teacher-info__stat">
					<strong>{{ displayLikes }}</strong>
					<span>{{ t(`${I18N_NS}.likes`) }}</span>
				</div>
				<div class="teacher-info__stat">
					<strong>{{ displayFans }}</strong>
					<span>{{ t(`${I18N_NS}.followers`) }}</span>
				</div>
				<button
					type="button"
					class="teacher-info__share"
					:aria-label="t(`${I18N_NS}.share`)"
					@click="openSharePanel"
				>
					<img :src="shareIcon" alt="" aria-hidden="true" />
				</button>
			</div>

			<p v-if="profileIntro" class="teacher-info__intro">{{ profileIntro }}</p>

			<div class="teacher-info__actions">
				<button
					type="button"
					class="teacher-info__follow"
					:class="{ 'is-followed': isFollowed }"
					:disabled="followLoading"
					@click="handleFollow"
				>
					<span v-if="!isFollowed" class="teacher-info__follow-icon" aria-hidden="true">+</span>
					<span>{{ isFollowed ? t(`${I18N_NS}.followed`) : t(`${I18N_NS}.follow`) }}</span>
				</button>
				<button v-if="chatTeacherId" type="button" class="teacher-info__chat" @click="toChat">
					{{ chatButtonText }}
				</button>
			</div>
		</template>

		<template v-else>
			<div class="teacher-info__card">
				<div class="teacher-info__avatar-wrap teacher-info__avatar-wrap--card">
					<img class="teacher-info__avatar" :src="avatarSrc" :alt="profileName" />
				</div>

				<div class="teacher-info__name-row">
					<h1 class="teacher-info__name teacher-info__name--card">{{ profileName }}</h1>
				</div>

				<ul v-if="profileTagItems.length" class="teacher-info__tags" role="list">
					<li v-for="tag in profileTagItems" :key="tag.text" class="teacher-info__tag">
						{{ tag.text }}
					</li>
				</ul>

				<dl class="teacher-info__stats teacher-info__stats--card">
					<div class="teacher-info__stat-item">
						<dt>{{ t(`${I18N_NS}.yearsExperience`) }}</dt>
						<dd>{{ displayYear }}</dd>
					</div>
					<div class="teacher-info__stat-item">
						<dt>{{ t(`${I18N_NS}.serviceCount`) }}</dt>
						<dd>{{ displayAnswers }}</dd>
					</div>
					<div class="teacher-info__stat-item">
						<dt>{{ t(`${I18N_NS}.rating`) }}</dt>
						<dd>{{ displayScore }}</dd>
					</div>
				</dl>

				<p v-if="profileIntro" class="teacher-info__intro teacher-info__intro--card">
					{{ profileIntro }}
				</p>
			</div>
		</template>

		<div ref="privacyBannerRef" class="teacher-info__privacy">
			<div class="teacher-info__privacy-copy">
				<strong>{{ t(`${I18N_NS}.privacyTitle`) }}</strong>
				<span>{{ t(`${I18N_NS}.privacyDesc`) }}</span>
			</div>
			<img class="teacher-info__privacy-icon" :src="secureIcon" alt="" aria-hidden="true" />
		</div>
		<SeerShareModal
			v-model:show="showSharePanel"
			:share-list="shareList"
			@share="handleShare"
			@copy="handleCopyLink"
		/>
	</section>
</template>

<script setup>
import { computed, inject, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { trackSquare } from "~/api/api";
import { useShare } from "~/hooks/share/index";
import { useI18n } from "~/composables/useI18n";
import { copy } from "~/utils/clipboard";
import { useInjectedMentorDetailTracking } from "@variants/seer/teacher-detail/composables/useMentorDetailTracking";
import SeerShareModal from "@variants/seer/components/share-modal/index.vue";
import defaultAvatar from "~/assets/images/default_avatar.png";
import secureIcon from "../../../pc/images/secure.png";
import shareIcon from "../../images/icon-share.svg";

const I18N_NS = "teacherDetail.pc";
const { t, locale } = useI18n();
const openLogin = inject("openLogin");
const tracking = useInjectedMentorDetailTracking();
const impressionTracker = tracking?.createImpressionTracker?.();

const props = defineProps({
	data: {
		type: Object,
		default: () => ({}),
	},
	profileMode: {
		type: String,
		default: "teacher",
		validator: value => ["teacher", "creator"].includes(value),
	},
});

const followLoading = ref(false);
const shareList = ref([]);
const showSharePanel = ref(false);
const profileSectionRef = ref(null);
const privacyBannerRef = ref(null);

const hasProfile = computed(() => Boolean(props.data && Object.keys(props.data).length));
const isAreaTeacher = computed(() => Number(props.data?.area_type) === 10);
const isCreatorMode = computed(() => props.profileMode === "creator" && !isAreaTeacher.value);
const isTeacherMode = computed(() => props.profileMode === "teacher" && !isAreaTeacher.value);
const isEnglish = computed(() => locale.value === "en");
const profileName = computed(
	() =>
		(isEnglish.value ? props.data?.nickname_en : props.data?.nickname) || props.data?.nickname || ""
);
const avatarSrc = computed(() => props.data?.avatar || defaultAvatar);
const areaHeaderImage = computed(() =>
	isAreaTeacher.value ? props.data?.big_teacher_image || "" : ""
);
const displayLikes = computed(() => formatCount(props.data?.like));
const displayFans = computed(() => formatCount(props.data?.fans));
const isFollowed = computed(
	() =>
		props.data?.is_follow === true || props.data?.is_follow === 1 || props.data?.is_follow === "1"
);

const profileTagItems = computed(() =>
	normalizeTagItems(
		isEnglish.value
			? props.data?.tags_en ||
					props.data?.academic_sector_en ||
					props.data?.area_tag_en ||
					props.data?.label_en ||
					props.data?.tags ||
					props.data?.academic_sector ||
					props.data?.area_tag ||
					props.data?.label
			: props.data?.tags || props.data?.academic_sector || props.data?.area_tag || props.data?.label
	).slice(0, isAreaTeacher.value ? 3 : 4)
);

const profileIntro = computed(
	() =>
		(isEnglish.value
			? props.data?.introduce_en ||
				props.data?.introduction_en ||
				props.data?.creator_introduction_en
			: props.data?.introduce) ||
		props.data?.introduce ||
		""
);

const displayYear = computed(() => {
	const value = props.data?.year;
	if (!value) return "--";
	if (!isEnglish.value) return value;
	const numberText = String(value).match(/[\d.]+/)?.[0];
	return numberText ? `${numberText}years` : value;
});
const displayAnswers = computed(() => props.data?.answers ?? "--");
const displayScore = computed(() => props.data?.score || "5.0");

const isAssistantChat = computed(
	() =>
		(isAreaTeacher.value || props.data?.is_big_teacher) && isEnabledFlag(props.data?.is_assistant)
);

const chatTeacherId = computed(() => {
	if (isAreaTeacher.value || props.data?.is_big_teacher) {
		if (isAssistantChat.value) {
			return normalizeChatId(
				props.data?.big_teacher_assistant_str || props.data?.big_teacher_assistant
			);
		}
		if (isEnabledFlag(props.data?.is_free_chat)) {
			return normalizeChatId(props.data?.uid);
		}
		return "";
	}
	return normalizeChatId(props.data?.uid);
});

const chatButtonText = computed(() =>
	isAssistantChat.value ? t(`${I18N_NS}.freeAssistantConsult`) : t(`${I18N_NS}.privateChat`)
);

watch(
	() => props.data?.uid,
	uid => {
		if (!uid || !import.meta.client || !isCreatorMode.value) return;
		shareList.value = useShare({
			link: window.location.href,
			text: profileName.value,
			img: avatarSrc.value,
		});
	},
	{ immediate: true }
);

function formatCount(value) {
	if (value === undefined || value === null || value === "") return "--";
	const numberValue = Number(value);
	if (!Number.isFinite(numberValue)) return String(value);
	return numberValue.toLocaleString();
}

function isEnabledFlag(value) {
	return value === true || value === 1 || value === "1" || value === "true";
}

function normalizeChatId(value) {
	if (value === undefined || value === null) return "";
	const normalized = String(value).trim();
	return normalized && normalized !== "0" ? normalized : "";
}

function normalizeTagItems(source) {
	if (Array.isArray(source)) {
		return source
			.filter(Boolean)
			.map(item => normalizeTagItem(item))
			.filter(item => item.text);
	}

	if (typeof source === "string") {
		return source
			.split(/[,，|]/)
			.map(item => normalizeTagItem(item))
			.filter(item => item.text);
	}

	return [];
}

function normalizeTagItem(item) {
	if (!item) return { text: "" };
	if (typeof item === "object") {
		return {
			text: String(item.text || item.name || item.label || item.title || "").trim(),
		};
	}
	return { text: String(item).trim() };
}

function checkLogin() {
	if (!import.meta.client) return false;
	if (!localStorage.getItem("token")) {
		openLogin?.();
		return false;
	}
	return true;
}

async function handleFollow() {
	if (!import.meta.client || followLoading.value || isFollowed.value) return;
	if (!checkLogin()) return;

	const uid = props.data?.uid;
	if (!uid) return;

	followLoading.value = true;
	const prevFollowed = isFollowed.value;
	props.data.is_follow = true;

	try {
		const res = await trackSquare({ uid: String(uid) });
		const result = res?.data?.[0];
		if (result) {
			props.data.is_follow = result.is_follow === true || result.is_follow === 1;
			if (result.fans !== undefined) {
				props.data.fans = result.fans;
			}
		}
	} catch (error) {
		props.data.is_follow = prevFollowed;
		console.error("追蹤失败:", error);
	} finally {
		followLoading.value = false;
	}
}

function openSharePanel() {
	tracking?.track?.("mentor_share_click", {
		share_channel: "panel_open",
	});
	showSharePanel.value = true;
}

function handleShare(item) {
	tracking?.track?.("mentor_share_click", {
		share_channel: item?.name || item?.type || "unknown",
	});
	item?.shareFn?.();
}

function handleCopyLink() {
	if (!import.meta.client) return;
	tracking?.track?.("mentor_share_click", {
		share_channel: "copy_link",
	});
	copy(`${profileName.value} ${window.location.href}`, "", () => {
		showToast({
			message: t("square.detail.copyLinkSuccess"),
			type: "success",
		});
	});
}

const toChat = async () => {
	if (import.meta.client) {
		const { logEvent } = await import("~/utils/firebase");
		logEvent("site_masterpage_ask");
	}
	tracking?.track?.("mentor_free_consult_click", {
		cta_position: "header",
	});

	openLogin?.();
	if (!chatTeacherId.value) return;
	await navigateTo(`/seer/chat/${chatTeacherId.value}`);
};

watch(
	() => [hasProfile.value, props.data?.uid, isCreatorMode.value],
	async () => {
		if (!hasProfile.value || !impressionTracker || isCreatorMode.value) return;
		await nextTick();
		const mentorId = String(props.data?.uid || "");
		if (profileSectionRef.value) {
			impressionTracker.bind(
				profileSectionRef.value,
				`profile_${mentorId}`,
				"mentor_profile_section_view",
				{
					profile_completeness: tracking.getProfileCompleteness(props.data),
				}
			);
		}
		if (privacyBannerRef.value) {
			impressionTracker.bind(
				privacyBannerRef.value,
				`privacy_${mentorId}`,
				"mentor_privacy_banner_view"
			);
		}
	},
	{ immediate: true }
);

onBeforeUnmount(() => {
	impressionTracker?.reset?.();
});
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	overflow-wrap: anywhere;
}

.teacher-info {
	width: 100%;
	font-family: "Source Han Serif CN", "PingFang SC", sans-serif;

	&--creator {
		padding: 24px 32px 0;
	}

	&--teacher {
		padding: 108px 32px 0;
	}

	&__profile {
		display: flex;
		align-items: center;
		gap: 16px;
		min-width: 0;
	}

	&__avatar-wrap {
		flex: 0 0 auto;
		width: 96px;
		height: 96px;
		padding: 1px;
		border: 1px solid #fff3da;
		border-radius: 50%;
		background: linear-gradient(180deg, #fff4dc 0%, #ffffff 100%);
		overflow: hidden;

		&--card {
			position: absolute;
			left: 50%;
			top: -56px;
			width: 112px;
			height: 112px;
			padding: 2px;
			border: 0;
			background: #fff2bf;
			transform: translateX(-50%);
		}
	}

	&__avatar {
		display: block;
		width: 100%;
		height: 100%;
		border-radius: 50%;
		object-fit: cover;
	}

	&__name {
		flex: 1;
		min-width: 0;
		margin: 0;
		color: #333333;
		font-family: "PingFang SC", sans-serif;
		font-size: 32px;
		font-weight: 600;
		line-height: 32px;
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;

		&--card {
			max-width: 100%;
			font-size: 40px;
			font-weight: 700;
			line-height: 1.2;
			white-space: nowrap;
			display: block;
			-webkit-line-clamp: unset;
		}
	}

	&__name-row {
		display: flex;
		align-items: center;
		justify-content: center;
		min-width: 0;
	}

	&__stats-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-top: 32px;
		padding-right: 96px;
	}

	&__stat {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 16px;
		min-width: 72px;

		strong {
			color: #333333;
			font-family: "PingFang SC", sans-serif;
			font-size: 32px;
			font-weight: 600;
			line-height: 32px;
		}

		span {
			color: #a0a0a0;
			font-family: "PingFang SC", sans-serif;
			font-size: 24px;
			line-height: 24px;
		}
	}

	&__share {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 48px;
		height: 48px;
		padding: 0;
		border: 0;
		background: transparent;

		img {
			display: block;
			width: 48px;
			height: 48px;
		}
	}

	&__intro {
		margin: 24px 0 0;
		color: #333333;
		font-size: 28px;
		font-weight: 500;
		line-height: 1.5;
		word-break: break-word;

		&--card {
			margin: 40px 0 0;
			font-family: "PingFang SC", sans-serif;
			font-size: 26px;
			font-weight: 400;
			line-height: 40px;
			@include line-clamp(7);
		}
	}

	&__actions {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 22px;
		margin-top: 32px;
	}

	&__follow {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 4px;
		flex: 1;
		max-width: 500px;
		height: 68px;
		padding: 0 24px 0 16px;
		border: 0;
		border-radius: 20px;
		background: #f2af19;
		color: #ffffff;
		font-size: 28px;
		font-weight: 700;
		line-height: 40px;

		&.is-followed {
			background: #f6f6f6;
			color: #a0a0a0;
		}
	}

	&__follow-icon {
		font-size: 32px;
		line-height: 1;
	}

	&__chat {
		flex: 0 0 164px;
		height: 68px;
		padding: 0 34px;
		border: 1px solid #333333;
		border-radius: 20px;
		background: #ffffff;
		color: #333333;
		font-size: 32px;
		font-weight: 700;
		line-height: 40px;
	}

	&__card {
		position: relative;
		width: 100%;
		padding: 108px 32px 40px;
		border: 1px solid #efefef;
		border-radius: 24px;
		background: #ffffff;
	}

	&__tags {
		display: flex;
		flex-wrap: nowrap;
		justify-content: center;
		gap: 16px;
		width: 100%;
		margin: 32px 0 0;
		padding: 0;
		overflow: hidden;
		list-style: none;
	}

	&__tag {
		flex: 0 1 auto;
		max-width: 180px;
		height: 48px;
		padding: 0 20px;
		border-radius: 4px;
		background: rgba(242, 175, 25, 0.1);
		color: #f2af19;
		font-size: 28px;
		font-weight: 400;
		line-height: 48px;
		text-align: center;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__stats--card {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 24px;
		margin: 48px 0 0;
	}

	&__stat-item {
		display: flex;
		flex-direction: column-reverse;
		align-items: center;
		min-width: 0;
		margin: 0;

		dt {
			width: 100%;
			margin-top: 14px;
			color: #595959;
			font-family: "PingFang SC", sans-serif;
			font-size: 24px;
			line-height: 1.2;
			text-align: center;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		dd {
			width: 100%;
			margin: 0;
			color: #333333;
			font-size: 36px;
			font-weight: 700;
			line-height: 1.1;
			text-align: center;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}

	&__area-card {
		position: relative;
		width: 100%;
		height: 306px;
		border-radius: 24px;
		overflow: hidden;
	}

	&__area-image,
	&__area-fallback {
		position: absolute;
		inset: 0;
		display: block;
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	&__area-content {
		position: absolute;
		top: 36px;
		right: 28px;
		bottom: 24px;
		left: 286px;
		min-width: 0;
		overflow: hidden;
	}

	&__area-name {
		margin: 0;
		color: #333333;
		font-size: 36px;
		font-weight: 700;
		line-height: 1.2;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__area-tags {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		margin: 14px 0 0;
		padding: 0;
		list-style: none;
		overflow: hidden;
	}

	&__area-tag {
		max-width: 150px;
		height: 32px;
		padding: 0 14px;
		border-radius: 4px;
		background: rgba(242, 175, 25, 0.1);
		color: #f2af19;
		font-size: 22px;
		line-height: 32px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__area-intro {
		margin: 14px 0 0;
		color: #333333;
		font-family: "PingFang SC", sans-serif;
		font-size: 22px;
		line-height: 32px;
		@include line-clamp(4);
	}

	&__privacy {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 20px;
		min-height: 96px;
		margin: 24px 0 0;
		padding: 18px 24px;
		border-radius: 12px;
		background: #eef5ff;
		overflow: hidden;
	}

	&--creator &__privacy {
		margin-top: 32px;
	}

	&--area &__privacy-copy {
		width: calc(100% - 238px);
		margin-right: 24px;
		margin-left: 24px;
	}

	&__privacy-copy {
		position: relative;
		z-index: 1;
		display: flex;
		width: calc(100% - 190px);
		min-width: 0;
		flex-direction: column;
		gap: 8px;

		strong {
			color: #1c6ee8;
			font-size: 28px;
			font-weight: 700;
			line-height: 1.15;
		}

		span {
			color: #333333;
			font-family: "PingFang SC", sans-serif;
			font-size: 22px;
			line-height: 1.35;
			@include line-clamp(3);
		}
	}

	&__privacy-icon {
		position: absolute;
		right: -6px;
		bottom: 0;
		width: 187px;
		height: auto;
		max-width: 42%;
	}

	&--area {
		padding-top: 22px;
	}
}
</style>
